import { ReactEditor } from 'slate-react';

export function getAlignmentMarkStyles(element) {

  const alignmentType = element.alignment;

  if (typeof alignmentType !== 'string') {
    return {};
  }

  return { textAlign: alignmentType };

}

export function getLayoutStyles(element) {

  const styles = {
    padding: getNormalizedFourSideValues(element.padding),
    ...getNormalizedMargin(element),
    borderRadius: getNormalizedFourSideValues(element.radius),
    borderWidth: null,
    borderColor: null,
    borderStyle: null,
    width: null,
    height: null
  };

  const border = element.border;
  const width = element.width;
  const height = element.height;

  if (typeof border !== 'undefined' && border !== null) {

    const borderWidth = getNormalizedFourSideValues(border.width);

    styles.borderWidth = borderWidth;
    styles.borderStyle = border.style;
    styles.borderColor = border.color;

  }

  if (typeof width === 'undefined') {
    styles.width = 'auto';
  } else if (typeof width === 'number') {
    if (width === 0) {
      styles.width = 'auto';
    } else {
      styles.width = `${width}px`;
    }
  } else {
    styles.width = `${width}`;
  }

  if (typeof height === 'undefined') {
    styles.height = 'auto';
  } else if (typeof height === 'number') {
    if (height === 0) {
      styles.height = 'auto';
    } else {
      styles.height = `${height}px`;
    }
  } else {
    styles.height = `${height}`;
  }

  return styles;

}

function plainValueToCSSNumericValue(plainValue, keepZero = false) {

  if (typeof plainValue === 'number') {
    if (plainValue === 0) {
      if (keepZero === true) {
        return '0px';
      }
      return 'auto';
    } else {
      return `${plainValue}px`;
    }
  }

  return plainValue;

}

function getBoxShadowCSS(boxShadow) {

  const keepZero = true;

  const horizontalOffset = plainValueToCSSNumericValue(boxShadow.horizontalOffset, keepZero);
  const verticalOffset = plainValueToCSSNumericValue(boxShadow.verticalOffset, keepZero);
  const blurRadius = plainValueToCSSNumericValue(boxShadow.blurRadius, keepZero);
  const spreadRadius = plainValueToCSSNumericValue(boxShadow.spreadRadius, keepZero);
  const color = boxShadow.color;

  return `${horizontalOffset} ${verticalOffset} ${blurRadius} ${spreadRadius} ${color}`;

}

export function getVisualStyles(element) {

  const styles = {
    color: null,
    backgroundColor: null,
    boxShadow: null
  };

  const textColor = element.textColor;
  const backgroundColor = element.backgroundColor;
  const boxShadow = element.boxShadow;
  const backgroundImage = element.backgroundImage;
  const backgroundPosition = element.backgroundPosition;
  const backgroundSize = element.backgroundSize;

  if (typeof textColor !== 'undefined' && textColor !== null) {
    styles.color = textColor;
  }

  if (typeof backgroundColor !== 'undefined' && backgroundColor !== null) {
    styles.backgroundColor = backgroundColor;
  }

  if (typeof boxShadow !== 'undefined' && boxShadow !== null) {
    styles.boxShadow = getBoxShadowCSS(boxShadow);
  }

  if (backgroundImage !== void 0) {
    styles.backgroundImage = backgroundImage;
  }

  if (backgroundPosition !== void 0) {
    styles.backgroundPosition = backgroundPosition;
  }

  if (backgroundSize !== void 0) {
    styles.backgroundSize = backgroundSize;
  }

  return styles;

}

export function getStyles(element) {

  return {
    ...getAlignmentMarkStyles(element),
    ...getLayoutStyles(element),
    ...getVisualStyles(element)
  };

}

export function getStylesV2(cssText) {
  const el = document.createElement('div');
  el.style.cssText = cssText;

  const out = {};
  for (const cssProp of el.style) {
    // cssProp is kebab-case here
    const jsProp = cssProp.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    out[jsProp === 'float' ? 'cssFloat' : jsProp] = el.style.getPropertyValue(cssProp).trim();
  }

  // Also include CSS variables if present
  // (They are not enumerable via `for (const cssProp of el.style)` in some browsers)
  const raw = el.getAttribute('style') || '';
  raw.split(';').forEach(part => {
    const [k, ...rest] = part.split(':');
    if (!k) return;
    const key = k.trim();
    if (key.startsWith('--')) out[key] = rest.join(':').trim();
  });

  return out;
}

function getNormalizedFourSideValues(values) {

  let _values = [];

  if (Array.isArray(values) === false) {
    return '0px';
  }

  const valuesLength = values.length;

  for (let index = 0; index < valuesLength; index++) {

    let value = values[index];

    if (typeof value === 'number') {
      if (value === 0) {
        _values.push(0);
      } else {
        _values.push(`${value}px`);
      }
    } else {
      _values.push(value);
    }

  }

  return _values.join(' ');

}

function getNormalizedMargin(element) {

  let margin = element.margin;
  let _margin = [];
  let marginLeft = element.marginLeft;
  let marginRight = element.marginRight;

  if (Array.isArray(margin) === false) {
    return { margin: ['0px', '0px', '0px', '0px'].join(' ') };
  }

  const marginLength = margin.length;

  for (let index = 0; index < marginLength; index++) {

    let value = margin[index];

    if (typeof value === 'number') {
      if (value === 0) {
        _margin.push('auto');
      } else {
        _margin.push(`${value}px`);
      }
    } else {
      _margin.push(value);
    }

  }

  if (marginLeft === 'auto' && marginRight === 'auto') {} else if (marginRight === 'auto') {
    marginLeft = 'unset';
  } else if (marginLeft === 'auto') {
    marginRight = 'unset';
  }

  const result = { margin: _margin.join(' '), marginLeft, marginRight };

  if (marginLeft === void 0) {
    delete result.marginLeft;
  }

  if (marginLeft === void 0) {
    delete result.marginRight;
  }

  return result;

}

export function positionToolbarColorOptions(element, viewportReference, options) {

  let anchorRectangle = options;

  const position = computeEditorOptionsPosition(element, anchorRectangle, viewportReference);

  const left = position.left + (anchorRectangle.width - element.offsetWidth);

  element.style.top = `${position.top}px`;
  element.style.left = `${left}px`;

  return position;

}

export function positionEditorOptions(element, viewportReference, options) {

  let anchorRectangle = options.anchorRectangle;

  const position = computeEditorOptionsPosition(element, anchorRectangle, viewportReference);

  element.style.top = `${position.top}px`;
  element.style.left = `${position.left}px`;

  return position;

}

export function positionNodeMenu(element, viewportReference, options) {

  const position = positionEditorOptions(element, viewportReference, options);

  element.style.height = `${position.height}px`;

}

export function positionTextColorOptionsSet(element, editorMainReference, options) {

  const position = computeMenuPosition(element, options.anchorRectangle, editorMainReference);

  let topOffset = options.topOffset;

  if (typeof topOffset !== 'number') {
    topOffset = 0;
  }

  element.style.top = `${position.top + topOffset}px`;
  element.style.left = `${position.left}px`;

}

export function positionElementNearSelection(element, selectionRectangle, viewportReference) {

  const position = computeEditorOptionsPosition(element, selectionRectangle, viewportReference);

  element.style.top = `${position.top}px`;
  element.style.left = `${position.left}px`;

}

function computeMenuPosition(element, selectionBoundingRectangle, editorMainReference) {

  const spacing = 10;

  let top = selectionBoundingRectangle.top + (selectionBoundingRectangle.height + spacing);
  let left = selectionBoundingRectangle.left;

  const editorDimensions = getEditorDimensions(editorMainReference);
  const editorBoundingRectangle = editorDimensions.boundingRectangle;

  if ((top + element.offsetHeight + spacing) > (editorBoundingRectangle.top + editorBoundingRectangle.height)) {
    top = selectionBoundingRectangle.top - (element.offsetHeight + spacing);
  }

  if (top < 0) {
    top = selectionBoundingRectangle.top + (selectionBoundingRectangle.height + spacing);
  }

  return {
    top,
    left
  };

}

export function computeEditorOptionsPosition(element, anchorRectangle, viewportReference, _spacing = 10) {

  const viewportBoundingRectangle = viewportReference.getBoundingClientRect();

  const spacing = _spacing;

  let top = anchorRectangle.top + (anchorRectangle.height + spacing);
  let left = anchorRectangle.left;
  let height = element.offsetHeight;

  let topSpace = anchorRectangle.top - viewportBoundingRectangle.top;
  let bottomSpace = viewportBoundingRectangle.bottom - anchorRectangle.bottom;

  if (topSpace > bottomSpace) {

    top = anchorRectangle.top - (height + spacing);

    if (top < spacing) {
      top = spacing;
    }

    if (topSpace < height) {
      height = topSpace - (spacing + 10);
    }

  } else {

    top = anchorRectangle.bottom + spacing;

    if (bottomSpace < height) {
      height = bottomSpace - (spacing + 10);
    }

  }

  return {
    top,
    left,
    height,
    topSpace,
    bottomSpace
  };

}

export function getSelection() {

  const domSelection = window.getSelection();
  if (domSelection.rangeCount > 0) {
    const domRange = domSelection.getRangeAt(0);
    return domRange;
  } else {
    return null;
  }

}

export function getSelectionRectangle() {

  const domSelection = getSelection();
  if (domSelection !== null) {
    return domSelection.getBoundingClientRect();
  } else {
    return null;
  }

}

function getEditorDimensions(editorMainReference) {

  const editorMainElement = editorMainReference.current;

  if (editorMainElement === null) {
    return { top: 0, left: 0, width: 0, height: 0 };
  }

  return {
    boundingRectangle: editorMainElement.getBoundingClientRect(),
    scrollTop: editorMainElement.scrollTop
  };

}

function computeToolbarPosition(element, selectionBoundingRectangle, editorMainReference) {

  const spacing = 10;

  let top = selectionBoundingRectangle.top - (element.offsetHeight + spacing);
  let left = selectionBoundingRectangle.left;

  const editorDimensions = getEditorDimensions(editorMainReference);
  const editorBoundingRectangle = editorDimensions.boundingRectangle;

  // If default toolbar start X position is before the container start X, set toolbar start X to container start X.
  if (left < editorBoundingRectangle.left) {
    left = editorBoundingRectangle.left;
  }

  // If default toolbar end X position is after the container end X, set the toolbar end X to container end X.
  if ((left + element.offsetWidth) > (editorBoundingRectangle.left + editorBoundingRectangle.width)) {
    left = left - ((left + element.offsetWidth) - (editorBoundingRectangle.left + editorBoundingRectangle.width));
  }

  // If default toolbar start Y position is before the container start Y, set toolbar start Y to selection end Y.
  if (top < editorBoundingRectangle.top) {
    top = selectionBoundingRectangle.top + selectionBoundingRectangle.height + spacing;
  }

  // If default toolbar end Y position is after container end Y, set toolbar end Y to selection start Y.
  if ((top + element.offsetHeight + spacing) > (editorBoundingRectangle.top + editorBoundingRectangle.height)) {
    top = selectionBoundingRectangle.top + selectionBoundingRectangle.height + spacing;
  }

  // If default toolbar start X position is before the container start X, set toolbar start X to container start X.
  if (top < editorBoundingRectangle.top) {
    top = editorBoundingRectangle.top;
  }

  // If default toolbar end Y position is after container end Y, set toolbar end Y to end of container end Y.
  if ((top + element.offsetHeight + spacing) > (editorBoundingRectangle.top + editorBoundingRectangle.height)) {
    top = editorBoundingRectangle.top + editorBoundingRectangle.height;
  }

  return {
    top,
    left
  };

}

export function hasSpinner(nodes) {

  return JSON.stringify(nodes).indexOf('{"type":"spinner","children":[{"text":""}]}') !== -1;

}

export function createChildNodes(count) {

  const childNodes = [];

  for (let index = 0; index < count; index++) {

    childNodes.push({ text: '' });

  }

  return childNodes;

}

function copyComputedStyles(source, target) {
  const computed = window.getComputedStyle(source);

  for (const property of computed) {
    target.setAttribute(
      'style',
      `${target.getAttribute('style') || ''}${property}:${computed.getPropertyValue(property)};`
    );
  }

  const sourceChildren = Array.from(source.children);
  const targetChildren = Array.from(target.children);

  for (let i = 0; i < sourceChildren.length; i++) {
    copyComputedStyles(sourceChildren[i], targetChildren[i]);
  }
}

export function elementToSelfContainedHTML(element) {
  const clone = element.cloneNode(true);

  copyComputedStyles(element, clone);

  return clone.outerHTML;
}

export function elementsToSelfContainedHTML(elements, editor) {

  let html = '';

  for (let i = 0; i < elements.length; i++) {
    const domNode = ReactEditor.toDOMNode(editor, elements[i]);
    html = html + elementToSelfContainedHTML(domNode);
  }

  return html;

}